import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCurrentUser, 
  signInWithRedirect, 
  signOut,
  fetchUserAttributes
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUser();

    // Listen for auth events
    const hubListener = Hub.listen('auth', ({ payload }) => {
      console.log('Auth event in context:', payload.event);
      switch (payload.event) {
        case 'signIn':
          checkUser();
          break;
        case 'signOut':
          setUser(null);
          break;
        default:
          console.log('Unhandled auth event:', payload.event);
      }
    });

    return () => hubListener();
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      console.log('Checking current user...');
      
      const authUser = await getCurrentUser();
      console.log('Current user:', authUser);
      
      // Only try to get attributes if we have a user
      try {
        const userAttributes = await fetchUserAttributes();
        console.log('User attributes:', userAttributes);
        
        setUser({
          username: authUser.username,
          userId: authUser.userId,
          email: userAttributes.email || userAttributes['custom:user.mail'],
          userPrincipalName: userAttributes['custom:user.userprincipalname'],
          ...userAttributes
        });
      } catch (attrError) {
        console.log('Could not fetch attributes:', attrError);
        // Still set basic user info even if attributes fail
        setUser({
          username: authUser.username,
          userId: authUser.userId
        });
      }
    } catch (err) {
      // This is expected when user is not authenticated
      console.log('User not authenticated');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signInWithAzure = async () => {
    try {
      setError(null);
      console.log('Initiating Azure sign in...');
      
      // Get the provider name from your Cognito configuration
      // This should match the name you gave your SAML provider in Cognito
      await signInWithRedirect({
        provider: {
          custom: 'Entra' // Change this to match your provider name
        }
      });
    } catch (err) {
      // Also try without specifying provider
      try {
        console.log('Trying sign in without specific provider...');
        await signInWithRedirect();
      } catch (err2) {
        setError(err2.message);
        console.error('Sign in error:', err2);
      }
    }
  };

  const signOutUser = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (err) {
      setError(err.message);
      console.error('Sign out error:', err);
    }
  };

  const value = {
    user,
    loading,
    error,
    signInWithAzure,
    signOut: signOutUser,
    checkUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};